<template>
  <v-container fluid class="playback pa-3">

    <!-- Toolbar -->
    <v-card flat outlined class="mb-3">
      <v-card-text class="py-3 px-4">
        <div class="d-flex align-end flex-wrap" style="gap:12px">
          <div style="flex:1;min-width:180px">
            <div class="overline grey--text mb-1">Asset</div>
            <v-autocomplete
              v-model="form.assetImei"
              :items="assets"
              item-text="name"
              item-value="imei"
              dense outlined hide-details
              placeholder="Select asset…"
              clearable
            />
          </div>
          <div style="width:148px">
            <div class="overline grey--text mb-1">Date from</div>
            <v-text-field v-model="form.dateFrom" type="date" dense outlined hide-details/>
          </div>
          <div style="width:116px">
            <div class="overline grey--text mb-1">Time from</div>
            <v-text-field v-model="form.timeFrom" type="time" dense outlined hide-details/>
          </div>
          <div style="width:148px">
            <div class="overline grey--text mb-1">Date to</div>
            <v-text-field v-model="form.dateTo" type="date" dense outlined hide-details/>
          </div>
          <div style="width:116px">
            <div class="overline grey--text mb-1">Time to</div>
            <v-text-field v-model="form.timeTo" type="time" dense outlined hide-details/>
          </div>
          <v-btn
            color="primary" depressed
            :disabled="!form.assetImei"
            :loading="loading"
            @click="loadTrack"
          >
            <v-icon left small>mdi-play</v-icon> Load
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-row>
      <!-- Map + controls -->
      <v-col cols="12" md="9">
        <v-card flat outlined>

          <v-card-title class="subtitle-2 font-weight-medium py-2 px-4">
            <v-icon left small color="grey">mdi-map</v-icon>
            Playback
            <v-chip v-if="loaded" x-small color="blue lighten-4"
              text-color="blue darken-3" class="ml-2">
              {{ selectedAsset && selectedAsset.label }}
            </v-chip>
            <v-spacer/>
            <span class="caption grey--text">
              {{ loaded ? `${form.dateFrom} · ${points.length} points` : '' }}
            </span>
          </v-card-title>

          <v-divider/>

          <!-- Map -->
          <div style="position:relative;height:360px">
            <div ref="mapContainer" style="height:100%;width:100%"></div>

            <!-- Empty state -->
            <div v-if="!loaded && !loading"
              class="d-flex flex-column align-center justify-center"
              style="position:absolute;inset:0;background:rgba(245,245,245,.92)">
              <v-icon size="40" color="grey lighten-1">mdi-map-marker-off</v-icon>
              <div class="caption grey--text mt-2">Select an asset and load to begin playback</div>
            </div>

            <!-- Loading -->
            <div v-if="loading"
              class="d-flex flex-column align-center justify-center"
              style="position:absolute;inset:0;background:rgba(245,245,245,.92)">
              <v-progress-circular indeterminate color="primary" size="32"/>
              <div class="caption grey--text mt-3">Loading track data…</div>
            </div>

            <!-- Live overlays -->
            <template v-if="loaded && currentPoint">
              <div style="position:absolute;bottom:14px;left:14px;background:#fff;
                border:1px solid rgba(0,0,0,.12);border-radius:8px;padding:6px 14px;
                display:flex;flex-direction:column;align-items:center">
                <span style="font-size:20px;font-weight:500;line-height:1">
                  {{ currentPoint.speed }}
                </span>
                <span style="font-size:10px;color:#9e9e9e;text-transform:uppercase;
                  letter-spacing:.05em">km/h</span>
              </div>
              <div style="position:absolute;top:14px;right:14px;background:#fff;
                border:1px solid rgba(0,0,0,.12);border-radius:8px;padding:5px 12px;
                font-family:monospace;font-size:13px;font-weight:500">
                {{ formatTime(currentPoint.ts) }}
              </div>
            </template>
          </div>

          <v-divider/>

          <!-- Scrubber -->
          <div class="px-4 pt-3 pb-0">
            <div class="d-flex align-center" style="gap:10px">
              <span class="caption grey--text font-weight-medium" style="min-width:42px">
                {{ scrubStartLabel }}
              </span>
              <div style="flex:1;position:relative">
                <!-- event ticks under the slider -->
                <div style="position:absolute;left:0;right:0;top:50%;
                  transform:translateY(-50%);height:4px;pointer-events:none;z-index:1">
                  <div
                    v-for="(ev, i) in events" :key="i"
                    :style="{
                      position:'absolute',
                      left: ((ev.idx / (points.length - 1)) * 100) + '%',
                      width: '3px',
                      height: '4px',
                      background: ev.color,
                      transform: 'translateX(-50%)',
                    }"
                  />
                </div>
                <v-slider
                  v-model="scrubValue"
                  :max="points.length - 1"
                  :disabled="!loaded"
                  hide-details dense
                  color="primary"
                  track-color="grey lighten-2"
                  @input="onScrub"
                  @mousedown="stopPlayback"
                />
              </div>
              <span class="caption grey--text font-weight-medium" style="min-width:42px;text-align:right">
                {{ scrubEndLabel }}
              </span>
            </div>
          </div>

          <!-- Playback controls -->
          <div class="d-flex align-center justify-space-between px-4 pb-3 pt-1">
            <div class="d-flex align-center" style="gap:4px">
              <v-btn icon small :disabled="!loaded || scrubValue===0"
                @click="stepBack" title="Back 5 points">
                <v-icon small>mdi-skip-previous</v-icon>
              </v-btn>
              <v-btn icon small :disabled="!loaded || scrubValue===0"
                @click="prevPoint" title="Previous">
                <v-icon small>mdi-step-backward</v-icon>
              </v-btn>
              <v-btn
                icon :disabled="!loaded"
                color="primary"
                @click="togglePlay"
                :title="playing ? 'Pause' : 'Play'"
              >
                <v-icon>{{ playing ? 'mdi-pause' : 'mdi-play' }}</v-icon>
              </v-btn>
              <v-btn icon small :disabled="!loaded || scrubValue >= points.length - 1"
                @click="nextPoint" title="Next">
                <v-icon small>mdi-step-forward</v-icon>
              </v-btn>
              <v-btn icon small :disabled="!loaded || scrubValue >= points.length - 1"
                @click="stepForward" title="Forward 5 points">
                <v-icon small>mdi-skip-next</v-icon>
              </v-btn>
            </div>

            <div class="d-flex align-center" style="gap:8px">
              <span class="caption grey--text">Speed</span>
              <v-select
                v-model="playbackSpeed"
                :items="speedOptions"
                item-text="label"
                item-value="ms"
                dense outlined hide-details
                style="width:90px"
                @change="onSpeedChange"
              />
            </div>
          </div>

          <!-- Legend -->
          <div class="d-flex flex-wrap px-4 pb-3 pt-0"
            style="gap:14px;border-top:1px solid rgba(0,0,0,.06)">
            <div v-for="l in legend" :key="l.label"
              class="d-flex align-center caption grey--text" style="gap:5px;padding-top:10px">
              <div :style="l.style"/>
              {{ l.label }}
            </div>
          </div>

        </v-card>
      </v-col>

      <!-- Side panels -->
      <v-col cols="12" md="3">

        <!-- Current point stats -->
        <v-card flat outlined class="mb-3">
          <v-card-title class="subtitle-2 font-weight-medium py-2 px-4">
            <v-icon left small color="grey">mdi-information-outline</v-icon>
            Current point
          </v-card-title>
          <v-divider/>
          <v-card-text class="pa-3">
            <v-row dense class="mb-2">
              <v-col cols="6">
                <div class="stat-cell">
                  <div class="stat-lbl">Speed</div>
                  <div class="stat-num blue--text text--darken-3">
                    {{ currentPoint ? currentPoint.speed : '—' }}
                    <span v-if="currentPoint" class="caption">km/h</span>
                  </div>
                </div>
              </v-col>
              <v-col cols="6">
                <div class="stat-cell">
                  <div class="stat-lbl">Altitude</div>
                  <div class="stat-num">
                    {{ currentPoint ? currentPoint.altitude : '—' }}
                    <span v-if="currentPoint" class="caption">m</span>
                  </div>
                </div>
              </v-col>
            </v-row>
            <template v-if="currentPoint">
              <div v-for="f in pointFields" :key="f.key"
                class="d-flex justify-space-between py-1"
                style="border-bottom:1px solid rgba(0,0,0,.05)">
                <span class="caption grey--text">{{ f.key }}</span>
                <span class="caption font-weight-medium" :style="f.style||''">{{ f.val }}</span>
              </div>
            </template>
            <div v-else class="caption grey--text text-center py-2">
              Load a track to see point data
            </div>
          </v-card-text>
        </v-card>

        <!-- Trip summary -->
        <v-card flat outlined class="mb-3">
          <v-card-title class="subtitle-2 font-weight-medium py-2 px-4">
            <v-icon left small color="grey">mdi-chart-bar</v-icon>
            Trip summary
          </v-card-title>
          <v-divider/>
          <v-card-text class="pa-3">
            <div v-for="f in summaryFields" :key="f.key"
              class="d-flex justify-space-between py-1"
              style="border-bottom:1px solid rgba(0,0,0,.05)">
              <span class="caption grey--text">{{ f.key }}</span>
              <span class="caption font-weight-medium" :class="f.cls||''">{{ f.val }}</span>
            </div>
          </v-card-text>
        </v-card>

        <!-- Events -->
        <v-card flat outlined>
          <v-card-title class="subtitle-2 font-weight-medium py-2 px-4">
            <v-icon left small color="grey">mdi-bell-outline</v-icon>
            Events
          </v-card-title>
          <v-divider/>
          <v-card-text class="pa-2">
            <div v-if="!events.length" class="caption grey--text text-center py-2">
              No events on this track
            </div>
            <div
              v-for="(ev, i) in events" :key="i"
              class="d-flex align-center pa-1 mb-1"
              style="border-radius:6px;cursor:pointer;transition:background .12s"
              :style="activeEventIdx === i ? 'background:#e6f1fb' : ''"
              @click="jumpToEvent(ev.idx)"
              @mouseenter="$event.currentTarget.style.background='#f5f5f5'"
              @mouseleave="$event.currentTarget.style.background= activeEventIdx===i ? '#e6f1fb' : 'transparent'"
            >
              <div :style="`width:8px;height:8px;border-radius:50%;background:${ev.color};flex-shrink:0;margin-right:8px`"/>
              <div style="flex:1;min-width:0">
                <div class="caption font-weight-medium">{{ ev.label }}</div>
                <div class="caption grey--text" style="font-family:monospace">{{ formatTime(points[ev.idx] && points[ev.idx].ts) }}</div>
              </div>
              <div class="caption grey--text">
                {{ ev.speed > 0 ? ev.speed + ' km/h' : 'stopped' }}
              </div>
            </div>
          </v-card-text>
        </v-card>

      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from "moment";
import { searchAssets } from "@/services/assetsDb";
import { fetchPlayback } from "@/services/nodeReports"; 
import { mapMutations, mapState } from "vuex";
import common from './../mixins/common.js';

export default {
  name: 'Playback',

  data() {
    const today = new Date().toISOString().slice(0, 10)
    return {
      form: {
        assetImei: null,
        dateFrom: today,
        timeFrom: moment().subtract(60, 'minute').format('HH:mm'),
        dateTo:   today,
        timeTo:   moment().format('HH:mm'),
      }, 
      assets: [], // populated from your API
      points: [],
      events: [],
      summary: null,
      loading: false,
      loaded: false,
      playing: false,
      scrubValue: 0,
      playbackSpeed: 200,    // ms per point
      playInterval: null,
      map: null,
      polylines: [],
      vehicleMarker: null,

      speedOptions: [
        { label: '0.5×', ms: 400 },
        { label: '1×',   ms: 200 },
        { label: '2×',   ms: 100 },
        { label: '4×',   ms: 50  },
        { label: '8×',   ms: 25  },
      ],

      legend: [
        { label: 'Trail',      style: 'width:16px;height:2.5px;background:#85b7eb;border-radius:1px' },
        { label: 'Overspeed',  style: 'width:16px;height:2.5px;background:#e24b4a;border-radius:1px' },
        { label: 'Trip start', style: 'width:9px;height:9px;border-radius:50%;background:#e1f5ee;border:1.5px solid #1d9e75' },
        { label: 'Stoppage',   style: 'width:9px;height:9px;border-radius:50%;background:#faeeda;border:1.5px solid #ef9f27' },
      ],
    }
  },
computed: {
    ...mapState(["notification", "session", "selected_object"]) ,

    currentPoint() {
      return this.points[this.scrubValue] || null
    },

    selectedAsset() {
      return this.assets.find(a => a.imei === this.form.assetImei) || null
    },

    scrubStartLabel() {
      return this.points.length ? this.formatTime(this.points[0].ts).slice(0,5) : '--:--'
    },

    scrubEndLabel() {
      return this.points.length ? this.formatTime(this.points[this.points.length-1].ts).slice(0,5) : '--:--'
    },

    activeEventIdx() {
      if (!this.currentPoint) return -1
      return this.events.findIndex(e => Math.abs(e.idx - this.scrubValue) < 3)
    },

    pointFields() {
      const p = this.currentPoint
      if (!p) return []
      return [
        { key: 'Timestamp', val: this.formatTime(p.ts) },
        { key: 'Latitude',  val: parseFloat(p.lat).toFixed(5) + '°' },
        { key: 'Longitude', val: parseFloat(p.lng).toFixed(5) + '°' },
        { key: 'Heading',   val: p.angle + '° ' + this.headingLabel(p.angle) }, 
        { key: 'Point',     val: `${this.scrubValue + 1} / ${this.points.length}` },
      ]
    },

    summaryFields() {
      const s = this.summary
      if (!s) return [{ key: 'Load a track to see summary', val: '' }]
      return [
        { key: 'Total distance', val: s.dist + ' km' },
        { key: 'Moving time',    val: this.fmtSecs(s.moveSecs) },
        { key: 'Stopped time',   val: this.fmtSecs(s.stopSecs) },
        { key: 'Avg speed',      val: s.avgSpd + ' km/h' },
        { key: 'Max speed',      val: s.maxSpd + ' km/h', cls: s.maxSpd > 80 ? 'red--text text--darken-2' : '' },
        { key: 'Total points',   val: s.count },
      ]
    },
  },

  async created() { 
  },
 
  async mounted() {  
    this.assets = await searchAssets(this.session.account[0].username);
    this.initMap()
    // Arriving from Trips with a specific trip window → load it immediately.
    if (this.applyRouteQuery()) this.loadFromRoute();
  },

  beforeDestroy() {
    this.stopPlayback()
    if (this.map) this.map.remove()
  },
  watch: {
    // Trips links here (name: 'Playback') with the trip's asset + window.
    // Re-load when the query changes while this view is already open.
    "$route.query"(q) {
      if (this.$route.name === "Playback" && q && q.imei) {
        this.applyRouteQuery();
        this.$nextTick(() => this.loadFromRoute());
      }
    },
  },
mixins: [common], 
  methods: { 
    // Populate the form from query params (used when arriving from Trips).
    applyRouteQuery() {
      const q = this.$route.query || {};
      if (!q.imei) return false;
      this.form.assetImei = q.imei;
      if (q.dateFrom) this.form.dateFrom = q.dateFrom;
      if (q.timeFrom) this.form.timeFrom = q.timeFrom;
      if (q.dateTo)   this.form.dateTo   = q.dateTo;
      if (q.timeTo)   this.form.timeTo   = q.timeTo;
      return true;
    },

    // Load the trip window from the route. If the stored history has no points
    // in that exact window (timezone drift / very short trips), fall back to the
    // whole day so the user still gets a track instead of an empty map.
    async loadFromRoute() {
      await this.loadTrack();
      if (this.loaded && this.points.length === 0 && this.form.dateFrom) {
        this.form.timeFrom = "00:00";
        this.form.timeTo = "23:59";
        if (!this.form.dateTo) this.form.dateTo = this.form.dateFrom;
        await this.loadTrack();
      }
    },

    async loadTrack() {
      this.loading = true
      this.loaded  = false
      this.stopPlayback()
      this.clearMap()

      try { 
        // kensrin_server only — no PHP fallback.
        const rp = await fetchPlayback({
          imei:      this.form.assetImei,
          dateFrom:  this.form.dateFrom,
          timeFrom:  this.form.timeFrom,
          dateTo:    this.form.dateTo,
          timeTo:    this.form.timeTo
        });
        if (rp.status == 200) {
          this.points = rp.data;
          this.loaded = true;
        }

        this.summary = this.computeSummary(this.points)
        this.events  = this.buildEvents(this.points)
        this.scrubValue = 0
       
        this.$nextTick(() => this.renderTrail())
      } finally {
        this.loading = false
      }
    },

    initMap() {
      if (!window.L) return
      this.map = window.L.map(this.$refs.mapContainer, { zoomControl: true })
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap'
      }).addTo(this.map)
    },

    clearMap() {
      if (!this.map) return
      this.polylines.forEach(p => p.remove())
      this.polylines = []
      if (this.vehicleMarker) { this.vehicleMarker.remove(); this.vehicleMarker = null }
    },

    renderTrail() {
      if (!this.map || !this.points.length) return

      // Split into normal / overspeed segments for colour coding
      let segType = null, segCoords = []
      const flush = (type, coords) => {
        if (!coords.length) return
        const color = type === 'over' ? '#e24b4a' : '#85b7eb'
        const pl = window.L.polyline(coords, { color, weight: 3, opacity: 0.8 }).addTo(this.map)
        this.polylines.push(pl)
      }

      this.points.forEach(p => {
        const type = p.speed > 60 ? 'over' : 'normal'
        if (type !== segType) {
          flush(segType, segCoords)
          segType = type
          segCoords = segCoords.length ? [segCoords[segCoords.length - 1]] : []
        }
        segCoords.push([p.lat, p.lng])
      })
      flush(segType, segCoords)

      // Stoppage markers
      this.events.filter(e => e.type === 'stoppage').forEach(e => {
        const p = this.points[e.idx]
        const m = window.L.circleMarker([p.lat, p.lng], {
          radius: 5, color: '#854f0b', fillColor: '#ef9f27', fillOpacity: 0.9, weight: 1.5
        }).addTo(this.map)
        this.polylines.push(m)
      })

      // Vehicle marker (custom div icon)
      const icon = window.L.divIcon({
        className: '',
        html: `<div style="width:22px;height:22px;border-radius:50%;background:#185fa5;
          border:2.5px solid #fff;display:flex;align-items:center;justify-content:center;
          color:#fff;font-size:11px">●</div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      })
      const first = this.points[0]
      this.vehicleMarker = window.L.marker([first.lat, first.lng], { icon }).addTo(this.map)

      const bounds = window.L.polyline(this.points.map(p => [p.lat, p.lng])).getBounds()
      this.map.fitBounds(bounds, { padding: [32, 32] })
    },

    setPoint(idx) {
      this.scrubValue = Math.max(0, Math.min(this.points.length - 1, idx))
      if (this.vehicleMarker && this.currentPoint) {
        const p = this.currentPoint
        this.vehicleMarker.setLatLng([p.lat, p.lng])
      }
    },

    togglePlay() {
      this.playing ? this.stopPlayback() : this.startPlayback()
    },

    startPlayback() {
      if (this.scrubValue >= this.points.length - 1) this.scrubValue = 0
      this.playing = true
      this.tick()
    },

    stopPlayback() {
      this.playing = false
      if (this.playInterval) { clearTimeout(this.playInterval); this.playInterval = null }
    },

    tick() {
      if (!this.playing) return
      if (this.scrubValue >= this.points.length - 1) { this.stopPlayback(); return }
      this.setPoint(this.scrubValue + 1)
      this.playInterval = setTimeout(this.tick, this.playbackSpeed)
    },

    onSpeedChange() {
      if (this.playing) { this.stopPlayback(); this.startPlayback() }
    },

    onScrub() {
      this.setPoint(this.scrubValue)
    },

    nextPoint()    { this.setPoint(this.scrubValue + 1) },
    prevPoint()    { this.setPoint(this.scrubValue - 1) },
    stepForward()  { this.setPoint(this.scrubValue + 5) },
    stepBack()     { this.setPoint(this.scrubValue - 5) },
    jumpToEvent(idx) { this.stopPlayback(); this.setPoint(idx) },

    computeSummary(pts) {
      if (!pts.length) return null
      let moveSecs = 0, stopSecs = 0, maxSpd = 0, totalSpd = 0, dist = 0
      for (let i = 1; i < pts.length; i++) {
        const p = pts[i], pp = pts[i-1]
        const dLat = (p.lat - pp.lat) * 111
        const dLng = (p.lng - pp.lng) * 111 * Math.cos(p.lat * Math.PI / 180)
        dist += Math.sqrt(dLat*dLat + dLng*dLng)
        // Attribute the REAL elapsed time between consecutive fixes to moving vs
        // stopped. Previously this assumed a fixed 2-minute sampling interval
        // (moveSecs += 120), so "Moving time" was really (pointCount-1) x 2min
        // and ignored the actual trip window (a 6-min trip could read 34m).
        const t1 = Date.parse(p.ts), t0 = Date.parse(pp.ts)
        const dt = (isFinite(t1) && isFinite(t0)) ? Math.max(0, (t1 - t0) / 1000) : 0
        p.speed > 5 ? moveSecs += dt : stopSecs += dt
        if (p.speed > maxSpd) maxSpd = p.speed
        totalSpd += p.speed
      }
      return {
        dist: dist.toFixed(1),
        moveSecs: Math.round(moveSecs), stopSecs: Math.round(stopSecs),
        maxSpd: Math.round(maxSpd),
        avgSpd: Math.round(totalSpd / pts.length),
        count: pts.length,
      }
    },

    buildEvents(pts) {
      const evs = []
      let inOver = false, inStop = false
      pts.forEach((p, i) => {
        if (p.speed > 60 && !inOver) {
          evs.push({ type:'overspeed', label:'Overspeed', idx:i, speed:p.speed, color:'#e24b4a' })
          inOver = true
        } else if (p.speed <= 60) inOver = false
        if (p.speed === 0 && !inStop) {
          evs.push({ type:'stoppage', label:'Stoppage', idx:i, speed:0, color:'#ef9f27' })
          inStop = true
        } else if (p.speed > 0) inStop = false
      })
      return evs
    },

    fmtSecs(s) {
      const h = Math.floor(s/3600), m = Math.floor((s%3600)/60)
      return h > 0 ? `${h}h ${m}m` : `${m}m`
    },

    formatTime(ts) {
      if (!ts) return '--:--:--'
      return new Date(ts).toLocaleTimeString('en-GB', {
        hour:'2-digit', minute:'2-digit', second:'2-digit'
      })
    },

    headingLabel(deg) {
      return ['N','NE','E','SE','S','SW','W','NW','N'][Math.round(deg / 45)]
    },
  },
}
</script>

<style scoped>
.stat-cell {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 8px 10px;
}
.stat-lbl {
  font-size: 11px;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: .04em;
  margin-bottom: 3px;
}
.stat-num {
  font-size: 18px;
  font-weight: 500;
  line-height: 1.1;
}
</style>